// E1 — reconciliation action vocabulary (pure, root-safe).
//
// The reconciliation decide verbs + their plain-language before→after
// consequence copy (UX rule #3). Disposition verbs: the steward
// records a decision and the issue resolves (audited, 60s-undoable
// via reopen). Where a verb implies a figure/value change (adjust /
// accept-proposed) the actual write rides the owning domain's
// existing calc/approve/adjust workflow — the company system stays
// the source of truth; the E4–E9 domain PRs wire that per-domain on
// top of this vocabulary.
//
// Kept as a standalone pure module (imports nothing) so it's the
// single source of truth shared by DecideRail AND unit-tested in
// isolation — same pattern as cleanup-glossary / coachmark-state.

export type ReconVerb =
  | "match"
  | "accept-variance"
  | "adjust"
  | "verify"
  | "mark-historical"
  | "keep-current"
  | "accept-proposed";

export const RECON_VERBS: readonly ReconVerb[] = [
  "match",
  "accept-variance",
  "adjust",
  "verify",
  "mark-historical",
  "keep-current",
  "accept-proposed",
] as const;

const RECON_VERB_SET = new Set<string>(RECON_VERBS);

export function isReconVerb(v: string | null | undefined): v is ReconVerb {
  return typeof v === "string" && RECON_VERB_SET.has(v);
}

// Plain-language before→after, honest about the E1 disposition
// contract (issue resolves + audited + 60s reopen-undo) and, where a
// value change is implied, that the write goes through the owning
// system of record.
export const RECON_CONSEQUENCE: Readonly<Record<ReconVerb, string>> = {
  match:
    "Confirms these are the same and links them. The reconciliation issue is resolved and the decision is on the audit trail; reversible for 60 seconds (it reopens).",
  "accept-variance":
    "Accepts the flagged difference as expected — no value is changed. The issue is resolved with your acknowledgement recorded; reversible for 60 seconds.",
  adjust:
    "Records that the figure needs correcting and resolves this reconciliation issue with that note. The actual amount is changed in the owning system’s workflow — that system stays the source of truth. Reversible for 60 seconds.",
  verify:
    "Marks this verified. The issue is resolved and the verification is on the audit trail; reversible for 60 seconds.",
  "mark-historical":
    "Marks this record/cycle historical — kept for history, no longer active. Nothing is deleted; reversible for 60 seconds.",
  "keep-current":
    "Keeps the current Stewardship value and sets aside the proposed/imported alternative. No value changes; the issue is resolved; reversible for 60 seconds.",
  "accept-proposed":
    "Accepts the proposed/imported value as correct and resolves the issue. The value update itself is applied through the owning system’s workflow (system of record); reversible for 60 seconds.",
};
